// Dashboard data, one card at a time. The layout (every card's shell) comes
// back at once; each card is then fetched on its own, so cheap cards land
// quickly and a slow one fills in when it is ready. Mirrors
// crates/tres-api/src/handlers/dashboard.rs.

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useApiSession } from '@/lib/useApiSession';

export type Card =
  | {
      kind: 'count';
      id: string;
      title: string;
      subtitle: string;
      value: number;
      /** Section (@layer) the SPA groups this card under. Absent for custom cards. */
      group?: string;
      /** Dashboard tab this card renders on ("System" for built-ins). */
      dashboard: string;
      /** True for cards backed by the dashboard_cards table. Absent on built-ins. */
      custom?: boolean;
    }
  | {
      kind: 'distribution';
      id: string;
      title: string;
      subtitle: string;
      items: DistributionItem[];
      truncated: boolean;
      group?: string;
      dashboard: string;
      custom?: boolean;
    }
  | {
      kind: 'table';
      id: string;
      title: string;
      subtitle: string;
      columns: string[];
      rows: string[][];
      truncated: boolean;
      dashboard: string;
      custom?: boolean;
    }
  | {
      kind: 'shape';
      id: string;
      title: string;
      subtitle: string;
      statements: number;
      entities: number;
      relationships: number;
      group?: string;
      dashboard: string;
      custom?: boolean;
    }
  | {
      /** The share of a question set the ontology can answer (QROS). */
      kind: 'ontology_readiness';
      id: string;
      title: string;
      subtitle: string;
      /** Null until a question check has finished on this database. */
      fraction: number | null;
      bank_name: string | null;
      answerable: number;
      questions: number;
      terms_required: number;
      rules_required: number;
      group?: string;
      dashboard: string;
      custom?: boolean;
    }
  | {
      /** The share of facts that do not conflict with the ontology (QROS). */
      kind: 'data_readiness';
      id: string;
      title: string;
      subtitle: string;
      fraction: number;
      conflicting: number;
      statements_checked: number;
      group?: string;
      dashboard: string;
      custom?: boolean;
    }
  | {
      /** The site at a glance, for Admins and Owners. */
      kind: 'admin';
      id: string;
      title: string;
      subtitle: string;
      /** UTC, as the state database writes it ("YYYY-MM-DD HH:MM:SS"). */
      last_updated_at: string | null;
      last_update: string | null;
      active_now: number;
      active_today: number;
      api_accounts_24h: number;
      api_last_call_at: string | null;
      model: string | null;
      model_provider: string | null;
      /** 'operator' or 'site': whose configuration chose the model. */
      model_source: string | null;
      group?: string;
      dashboard: string;
      custom?: boolean;
    };

export interface DistributionItem {
  label: string;
  uri: string;
  count: number;
}

/** A card's place on the page before its data arrives. */
export interface CardShell {
  id: string;
  kind: Card['kind'];
  title: string;
  subtitle: string;
  group?: string;
  dashboard: string;
  custom?: boolean;
}

export function useDashboardLayout() {
  const { token, ready } = useApiSession();
  return useQuery<CardShell[]>({
    queryKey: ['dashboard', 'layout', token ?? 'anon'],
    enabled: ready,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await apiFetch<{ cards: CardShell[] }>('/api/v1/dashboard/layout', token);
      if (!r.ok) throw new Error(r.message);
      return r.data.cards;
    },
  });
}

/** A card request that failed, with the HTTP status it failed with. */
export class CardRequestError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const INDEX_BUILDING_RETRIES = 20;

const indexBuilding = (error: Error) => error instanceof CardRequestError && error.status === 503;

/** One card's query. `null` when the card has nothing to show (a hidden card,
 *  or a layer with no statements). Shared by every component that shows the
 *  card, so the page asks the server once per card. */
export function dashboardCardQuery(
  id: string,
  { token, ready }: { token: string | undefined; ready: boolean },
) {
  return {
    queryKey: ['dashboard', 'card', id, token ?? 'anon'],
    enabled: ready,
    staleTime: 30_000,
    // A card is a count over the store; returning to the tab does not change it.
    refetchOnWindowFocus: false,
    // A card that reads the knowledge index answers 503 while the index
    // builds: the card stays in its loading state and asks again, 15 s, then
    // 30 s, then every minute, for about twenty minutes. A longer build ends
    // in a ready index, and VdbRebuildWatcher asks every card again then.
    retry: (failures: number, error: Error) =>
      indexBuilding(error) ? failures < INDEX_BUILDING_RETRIES : failures < 2,
    retryDelay: (failures: number, error: Error) =>
      indexBuilding(error) ? Math.min(15_000 * 2 ** failures, 60_000) : 1_000,
    queryFn: async (): Promise<Card | null> => {
      const r = await apiFetch<{ cards: Card[] }>(
        `/api/v1/dashboard/card/${encodeURIComponent(id)}`,
        token,
      );
      if (!r.ok) {
        throw new CardRequestError(r.message, r.status);
      }
      return r.data.cards[0] ?? null;
    },
  };
}

export function useDashboardCard(id: string) {
  return useQuery(dashboardCardQuery(id, useApiSession()));
}
